import axios from 'axios';
import { Storage } from 'src/_shared/storage/Storage';
import { StorageKeys } from 'src/_shared/storage/Storage.constants';
import {
  MEDIA_ITEMS_ENDPOINT,
  OAUTH_2_ENDPOINT,
  SCOPE_EMAIL,
  SCOPE_GOOGLE_PHOTO,
  SCOPE_PROFILE,
  SESSIONS_ENDPOINT,
  TOKEN_INFO_ENDPOINT,
  USER_INFO_ENDPOINT,
} from 'src/constants/google.constants';
import { EDITOR_BASE_URL } from 'src/app/settings';

export const oauthSignIn = ({ clientId, state }: { clientId: string; state: string }) => {
  // Create <form> element to submit parameters to OAuth 2.0 endpoint.
  const form = document.createElement('form');
  form.setAttribute('method', 'GET'); // Send as a GET request.
  form.setAttribute('action', OAUTH_2_ENDPOINT);

  // Parameters to pass to OAuth 2.0 endpoint.
  const params = {
    client_id: clientId,
    redirect_uri: EDITOR_BASE_URL,
    response_type: 'token',
    scope: [SCOPE_GOOGLE_PHOTO, SCOPE_PROFILE, SCOPE_EMAIL].join(' '),
    include_granted_scopes: 'true',
    state,
    prompt: 'select_account',
  };

  // Add form parameters as hidden input values.
  for (var p in params) {
    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', p);
    input.setAttribute('value', params[p as keyof typeof params]);
    form.appendChild(input);
  }

  // Add form to page and submit it to open the OAuth 2.0 endpoint.
  document.body.appendChild(form);
  form.submit();
};

export const startSession = ({
  accessToken,
}: {
  accessToken: string;
}): Promise<{ interval: number; sessionId: string; pickerUri: string }> => {
  return axios({
    method: 'post',
    url: SESSIONS_ENDPOINT,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  }).then(res => {
    const response = res.data;
    let pollInterval: string = response.pollingConfig.pollInterval;
    pollInterval = pollInterval.substring(0, pollInterval.length - 1);
    const suggestedInterval = parseInt(pollInterval) * 1000;
    return { pickerUri: response.pickerUri, interval: suggestedInterval, sessionId: response.id };
  });
};

export const pollSession = ({
  sessionId,
  accessToken,
}: {
  sessionId: string;
  accessToken: string;
}): Promise<boolean> => {
  return axios({
    method: 'get',
    url: `${SESSIONS_ENDPOINT}/${sessionId}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  }).then(res => {
    return res.data.mediaItemsSet;
  });
};

export const getAccountInfo = ({ accessToken }: { accessToken: string }) => {
  return axios({
    method: 'get',
    url: USER_INFO_ENDPOINT,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  }).then(res => {
    return res.data;
  });
};

const retrieveMediaItemsGoogleFoto = async (
  sessionId: string,
  accessToken: string,
  pageToken?: string,
  items: any[] = [],
): Promise<any[]> => {
  const url =
    `${MEDIA_ITEMS_ENDPOINT}?sessionId=${sessionId}&pageSize=100` + (pageToken ? `&pageToken=${pageToken}` : '');
  const res = await axios.get(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const { mediaItems = [], nextPageToken } = res.data;
  const updated = [...items, ...mediaItems];
  if (!nextPageToken) return updated;
  return retrieveMediaItemsGoogleFoto(sessionId, accessToken, nextPageToken, updated);
};

export const getMediaItems = ({ sessionId, accessToken }: { sessionId: string; accessToken: string }) => {
  return retrieveMediaItemsGoogleFoto(sessionId, accessToken);
};

export const getImageFromMediaItem = ({ accessToken, item }: { accessToken: string; item: any }) => {
  const url = (item.mediaFile.baseUrl as string).replace(
    'https://lh3.googleusercontent.com',
    '/lh3-googleusercontent-com',
  );

  // all images fetched in this way are in JPEG format
  return axios({
    method: 'get',
    url: `${url}=w${item.mediaFile.mediaFileMetadata.width}-h${item.mediaFile.mediaFileMetadata.height}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    responseType: 'arraybuffer',
  });
};

export const saveAccessToken = (accessToken: string, expireDate: string) => {
  Storage.setItem(StorageKeys.googleLoginAccessToken, accessToken);
  Storage.setItem(StorageKeys.googleLoginExpireDate, expireDate);
};

export const deleteAccessToken = () => {
  Storage.removeItem(StorageKeys.googleLoginAccessToken);
  Storage.removeItem(StorageKeys.googleLoginExpireDate);
};

export const isAccessTokenValid = () => {
  const expireDate = Storage.getItem(StorageKeys.googleLoginExpireDate) ?? '';
  return parseInt(expireDate) > Date.now();
};

export const readAccessToken = () => {
  if (isAccessTokenValid()) {
    return Storage.getItem(StorageKeys.googleLoginAccessToken) ?? '';
  } else {
    deleteAccessToken();
  }
};

export const checkScopes = (accessToken: string) => {
  return axios({
    method: 'get',
    url: `${TOKEN_INFO_ENDPOINT}?access_token=${accessToken}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  }).then(res => {
    return res.data;
  });
};
